import React, { useRef, useEffect, useCallback, ReactNode } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { closePopover, presetsClicked } from '../actions'
import { State } from 'shared'

const TARGET_OFFSET = 7 // pixels, make the popover move this many pixels away from the target
const VIEWPORT_PADDING = 15 // make room for the close icon
const NOTCH_SIZE = 10
const NOTCH_LONG_SIZE = 2 * NOTCH_SIZE

type Position = 'above' | 'below' | 'left' | 'right'

interface PopoverProps {
  className?: string
  width?: number | string
  maxWidth?: number | string
  minWidth?: number | string
  height?: number | string
  children: ReactNode
  show?: boolean
  alignTo: string
  position?: Position
  preventClose?: boolean
}

const Popover: React.FC<PopoverProps> = ({
  className,
  width,
  maxWidth,
  minWidth,
  height,
  children,
  show,
  alignTo,
  position,
  preventClose
}) => {
  const dispatch = useDispatch()
  const popover = useSelector((state: State) => state.popover)
  const popoverRef = useRef<HTMLDivElement>(null)
  const notchRef = useRef<HTMLDivElement>(null)

  const handleClose = useCallback(() => {
    if (popover === 'video') {
      dispatch(presetsClicked())
    } else {
      dispatch(closePopover())
    }
  }, [popover, dispatch])

  const align = useCallback(() => {
    const popover = popoverRef.current
    const notch = notchRef.current
    if (!popover) {
      throw new Error('Popover element not found.')
    }
    const root = findAncestor(popover, 'velorbis-customizer-root')
    const target = document.body.querySelector(alignTo)

    if (!target) {
      throw new Error(
        `Popover align error: Target element not found by selector '${alignTo}'.`
      )
    }

    const rootRect = root.getBoundingClientRect()
    const popoverRect = popover.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()

    let calculatedPosition = position

    if (!calculatedPosition) {
      const flexDirection = window
        .getComputedStyle(target.parentNode as Element)
        .getPropertyValue('flex-direction')
      if (flexDirection === 'column') {
        if (
          targetRect.left - rootRect.left >
          rootRect.right - targetRect.right
        ) {
          calculatedPosition = 'left'
        } else {
          calculatedPosition = 'right'
        }
      } else {
        if (
          targetRect.top - rootRect.top >
          rootRect.bottom - targetRect.bottom
        ) {
          calculatedPosition = 'above'
        } else {
          calculatedPosition = 'below'
        }
      }
    }

    let top: number | undefined,
      right: number | undefined,
      bottom: number | undefined,
      left: number | undefined
    let notchTop: number | undefined,
      notchRight: number | undefined,
      notchBottom: number | undefined,
      notchLeft: number | undefined

    if (calculatedPosition === 'above') {
      bottom = rootRect.top + rootRect.height - targetRect.top + TARGET_OFFSET
      notchBottom = -NOTCH_SIZE
    } else if (calculatedPosition === 'below') {
      top = targetRect.bottom + TARGET_OFFSET - rootRect.top
      notchTop = -NOTCH_SIZE
    } else {
      top = targetRect.top
      top = Math.min(
        top,
        rootRect.height - popoverRect.height - VIEWPORT_PADDING
      )
      top = Math.max(top, TARGET_OFFSET)
      top -= rootRect.top
      const notchPosition =
        targetRect.top + targetRect.height / 2 - top - rootRect.top - NOTCH_SIZE
      notchTop = Math.max(
        0,
        Math.min(popoverRect.height - NOTCH_LONG_SIZE, notchPosition)
      )
      top += notchPosition - notchTop
    }

    if (calculatedPosition === 'right') {
      left = targetRect.right + TARGET_OFFSET - rootRect.left
      notchLeft = -NOTCH_SIZE
    } else if (calculatedPosition === 'left') {
      throw new Error('Not implemented yet')
      // right = rootRect.left + rootRect.width - targetRect.left + TARGET_OFFSET
      // notchRight = -NOTCH_SIZE
    } else {
      left = targetRect.left + targetRect.width / 2 - popoverRect.width / 2
      left = Math.min(
        left,
        rootRect.width - popoverRect.width - VIEWPORT_PADDING
      )
      left = Math.max(left, VIEWPORT_PADDING)
      left -= rootRect.left
      notchLeft = targetRect.left + targetRect.width / 2 - left - NOTCH_SIZE
    }

    if (popover) {
      popover.style.top = toPx(top)
      popover.style.right = toPx(right)
      popover.style.bottom = toPx(bottom)
      popover.style.left = toPx(left)
    }

    if (notch) {
      notch.style.top = toPx(notchTop)
      notch.style.right = toPx(notchRight)
      notch.style.bottom = toPx(notchBottom)
      notch.style.left = toPx(notchLeft)
    }
  }, [alignTo, position])

  useEffect(() => {
    align()

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const popover = popoverRef.current
      if (popover && !popover.contains(event.target as Node)) {
        const cond = !popover.classList.value.includes('compatibility-popover') // prevent compatibility popup close on blur
        if (cond) {
          dispatch(closePopover())
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [align, dispatch])

  const _className = ['velorbis-customizer-popover', className || ''].join(' ')
  const style = {
    width: width ? width : 'auto',
    maxWidth: maxWidth ? maxWidth : 'unset',
    minWidth: minWidth ? minWidth : 'unset',
    height: height ? height : 'unset',
    display: !show && popover === 'video' ? 'none' : 'flex'
  }

  return (
    <div ref={popoverRef} className={_className} style={style}>
      <div ref={notchRef} className="velorbis-customizer-popover-notch" />
      <div className="velorbis-customizer-popover-inner">{children}</div>
      {!preventClose && (
        <div
          className="velorbis-customizer-popover-close"
          onClick={handleClose}
        >
          &#x2715;
        </div>
      )}
    </div>
  )
}

function toPx(v: number | undefined): string {
  return v != null ? v + 'px' : 'auto'
}

function findAncestor(el: HTMLElement, cls: string): HTMLElement {
  while (el && !el.classList.contains(cls)) {
    el = el.parentElement as HTMLElement
    if (!el) break
  }
  return el
}

export default Popover
